import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

/** Read-only recommendations. This component never changes cart contents or refreshes a section. */
class CartRecommendationsComponent extends Component {
  #life;
  #request;
  #key = '';
  #revision = 0;
  #queued = false;
  #frame;
  #cache = new Map();
  #observer;
  #resize;

  connectedCallback() {
    super.connectedCallback();
    this.#life = new AbortController();
    this.closest('cart-drawer-component')?.addEventListener(DialogOpenEvent.eventName, this.#schedule, {
      signal: this.#life.signal,
    });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal: this.#life.signal });
    this.#observer = new MutationObserver(this.#schedule);
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-seed-id', 'data-cart-ids', 'hidden'],
      childList: true,
      subtree: true,
    });
    this.#resize = new ResizeObserver(this.#layout);
    const dialog = this.closest('dialog');
    const summary = this.closest('.cart-drawer__summary');
    if (dialog) this.#resize.observe(dialog);
    if (summary) this.#resize.observe(summary);
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#life?.abort();
    this.#observer?.disconnect();
    this.#resize?.disconnect();
    this.#request?.abort();
    this.#revision += 1;
    cancelAnimationFrame(this.#frame);
  }

  #state() {
    let ids;
    try { ids = JSON.parse(this.dataset.cartIds || '[]'); } catch { ids = []; }
    const seed = this.dataset.seedId || '';
    if (!Array.isArray(ids) || !/^\d+$/.test(seed) || !ids.length) return null;
    ids = [...new Set(ids.map(String))].sort();
    if (!ids.includes(seed)) return null;
    return { seed, ids, key: `${seed}:${ids.join(',')}` };
  }

  #onCartUpdate = () => {
    // The existing cart handlers morph synchronously before this queued read of the mount's attributes.
    this.#revision += 1;
    this.#request?.abort();
    this.#schedule();
  };

  #schedule = () => {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      if (this.isConnected) void this.#refresh();
    });
  };

  async #refresh() {
    const state = this.#state();
    if (!state) {
      this.#revision += 1;
      this.#request?.abort();
      this.#key = '';
      if (!this.hidden) this.hidden = true;
      const results = this.querySelector('[data-recommendation-results]');
      if (results?.childNodes.length) results.replaceChildren();
      this.#layout();
      return;
    }
    if (state.key !== this.#key) {
      this.#revision += 1;
      this.#request?.abort();
      this.#key = state.key;
      this.hidden = true;
    }
    if (!this.closest('dialog')?.open) return;
    const cached = this.#cache.get(state.key);
    if (cached && Date.now() - cached.at < 60000) {
      this.#render(cached.html, state.key);
      return;
    }
    if (this.#request && !this.#request.signal.aborted) return;
    const controller = new AbortController();
    this.#request = controller;
    const revision = ++this.#revision;
    const deadline = setTimeout(() => controller.abort(), 12000);
    controller.signal.addEventListener('abort', () => clearTimeout(deadline), { once: true });
    this.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('product_id', state.seed);
      url.searchParams.set('section_id', 'cart-drawer-recommendations');
      url.searchParams.set('intent', 'related');
      url.searchParams.set('limit', '10');
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);
      const html = await response.text();
      if (!this.isConnected || controller.signal.aborted || revision !== this.#revision || this.#state()?.key !== state.key) return;
      const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-recommendation-fragment]');
      if (!fragment) throw new Error('Recommendation fragment missing');
      const seen = new Set(state.ids);
      for (const card of fragment.querySelectorAll('[data-recommendation-id]')) {
        const id = card.getAttribute('data-recommendation-id');
        if (!id || seen.has(id) || seen.size - state.ids.length >= 4) card.remove();
        else seen.add(id);
      }
      const result = fragment.querySelector('[data-recommendation-id]') ? fragment.innerHTML : '';
      this.#cache.set(state.key, { html: result, at: Date.now() });
      if (this.#cache.size > 4) this.#cache.delete(this.#cache.keys().next().value);
      this.#render(result, state.key);
    } catch (error) {
      if (revision === this.#revision && this.isConnected) {
        console.warn('[M&V recommendations]', error);
        this.#cache.set(state.key, { html: '', at: Date.now() });
        this.hidden = true;
        this.#layout();
      }
    } finally {
      clearTimeout(deadline);
      if (this.#request === controller) {
        this.#request = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  #render(html, key) {
    const results = this.querySelector('[data-recommendation-results]');
    if (!results || this.#state()?.key !== key) return;
    if (results.dataset.renderedKey === key && this.hidden === !html && results.innerHTML === html) return;
    // HTML is the dedicated theme-rendered fragment, never the header or cart response.
    results.innerHTML = html;
    results.dataset.renderedKey = key;
    this.hidden = !html;
    this.#layout();
  }

  #layout = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const dialog = this.closest('dialog');
      const summary = this.closest('.cart-drawer__summary');
      if (!this.isConnected || !dialog?.open || !summary) return;
      const height = dialog.getBoundingClientRect().height;
      if (!height) return;
      // Recheck Fabric's existing threshold after async content changes; never synthesize a dialog event.
      const sticky = summary.getBoundingClientRect().height / height <= 0.5 ? 'true' : 'false';
      if (dialog.getAttribute('cart-summary-sticky') !== sticky) dialog.setAttribute('cart-summary-sticky', sticky);
    });
  };
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendationsComponent);
}
